/**
 * ATL-1445 stage 3, P8 — migrating the v1 dual-write outbox into outbox v2 at the flip.
 *
 * Design: `docs/technical/write-cutover-stage3-app-seam.md` §2 "Migrating v1 entries at the flip" (the
 * spec), §9 risks 1, 2 and 10, and §16 (as built). Dark: it runs only for a session whose pin resolves
 * its organization to `'supabase'` (`kickV1MigrationAfterPin`), and no organization is Supabase-master.
 *
 * WHY. Before the flip, a write's Supabase leg is a MIRROR: when it fails it waits in the v1 outbox
 * (`global/Firebase/dualWriteOutbox.ts`), and v1 may drop it (7 days, 10 attempts, a 409, overflow) —
 * acceptable for a mirror of a write Firestore holds. After the flip the same entry is the only route
 * by which that write reaches the master: replayed by v1 it is a Firestore-first mirror, which backend
 * PR B1 refuses (409 `WRITE_MASTER_SUPABASE`) and v1 then drops as a permanent refusal. So at the first
 * Supabase-master boot every domain-W v1 entry of the organization moves into v2, where nothing is ever
 * dropped and every send is a master write.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE RULES (§2), AND HOW EACH IS MET
 *
 *  1. UNDER A LOCK ON `dualWriteOutbox.v1`. `withExclusiveV1Queue` holds the v1 DRAIN (in this JS
 *     context and, on web, across tabs) and the v1 QUEUE for the whole copy. A v1 entry that is being
 *     sent when the migration starts is therefore never also copied: the migration waits for that send
 *     to return and its outcome to be recorded (delivered → gone; failed → still queued, copied now;
 *     409-converted → already in v2 and gone from v1). No v1 drain can start until the copy is done,
 *     and a running one stops taking new entries as soon as the migration asks (review 1, S-4), so the
 *     LOCK wait is one replay (the 60 s write timeout), not a whole pass — bounded at 90 s in total for
 *     every lock, then `busy`. The copy itself is not bounded (an append cannot be abandoned safely);
 *     a watchdog pages after 150 s (review 2, N-a). No v2 drainer delivers while the migration copies:
 *     in this JS context each drainer checks the barrier before every entry (reviews 1–2, S-1 / SF-1),
 *     and on web the migration holds the v2 DRAIN Web Lock for its copy and v1 removal, so no other
 *     tab drains this organization meanwhile. A copy leaves v1 only at the end, and a delivered copy
 *     must not be replayed by v1 after a crash.
 *  2. EACH W ENTRY FOR THIS ORGANIZATION. W is the request's route through the registry
 *     (`classifyV1Request`, `v1ToV2.ts`). Entries of other organizations, entries outside W, and this
 *     organization's entries whose route cannot be read stay in v1 untouched (the last are reported).
 *  3. `entryId` = THE V1 ID (`v2EntryIdForV1`), `origin: 'v1-migrated'`.
 *  4. IN `enqueuedAt` ORDER, AT THE HEAD. Copied oldest first (ties: v1 queue position, which is
 *     enqueue order — the same tie-break v1's own drain needs, ATL-1194 note). "At the head" holds by
 *     order of operations (`v1MigrationBarrier.ts`): the migration is registered in the callback that
 *     pins the session, before any writer can run, and every live v2 append waits for it. So at copy
 *     time this organization's v2 queue can hold only (a) v1-derived entries from an earlier session —
 *     an earlier migration, or a 409 conversion — which per document are OLDER than anything still in
 *     v1 (v1 drains a document's entries in order and stops at its first failure), so appending after
 *     them keeps every document's order; and (b) live entries from an earlier session, which are newer
 *     than every pre-flip write that session's own migration moved. A candidate OLDER than a live entry
 *     already queued can only be a write that reached v1 after that migration (a stray Firestore-first
 *     write from another tab, a recovered mirror intent, a failed earlier copy): it is appended after
 *     it — exactly where §4 puts a stray mirror — and COUNTED (`orderAfterLive`, read AFTER the copy,
 *     so an append by another tab during it is counted too), never silently. The barrier stays up
 *     until the migration ends, including its bounded wait for the v1 drain.
 *  5. THEN REMOVED FROM V1 — once, at the end, under the same lock, and only the entries v2 holds: a
 *     copy that v2 reported durable, or one v2 already held (`existing === 'ok'`, a re-run after a
 *     crash). An entry whose copy failed stays in v1, under v1's rules.
 *  6. CRASH MIDWAY: the copy is append-then-remove, so a crash anywhere leaves every entry in v1, in
 *     v2, or in both — never in neither. The re-run skips what v2 already holds (duplicate entryId) and
 *     removes it from v1. If v2 had already DELIVERED such a copy before the crash, the re-run queues it
 *     once more: the backend's idempotency on the document id covers that double send (§2).
 *  7. ABSOLUTE STOCK MIRRORS (`products.updateStock` → `/products/stock/bulk`, and the single-product
 *     `PATCH /products/:id/stock`) go to the QUARANTINE (`outboxQuarantine.ts`, review 1 S-3), held
 *     (`absolute-replay`) with the full entry, never replayed, paged at once — and OUTSIDE the drain
 *     queue, so nothing ever waits behind them. Only an operator resolves them (`discardQuarantined`).
 *  8. NOT EVERYTHING CAN BE COPIED, and nothing is dropped for it:
 *      - an entry v2's validator refuses (not plain JSON, an unknown method, …) can NEVER be copied: it
 *        stays in v1 and is reported, and so do the LATER entries of the same document (they must not
 *        overtake it); the other documents continue, because waiting would not change its answer;
 *      - a STORAGE problem STOPS the copy, and everything not yet copied stays in v1: a plain append
 *        failure (the store refused: nothing was queued), an id held by an UNREADABLE record (an
 *        Android kill tore an earlier copy's file; P2 obligation 5: not landed, an operator removes
 *        it), or an append still indeterminate after a retry under the same id (P2 obligation 4). The
 *        next run resumes from that entry, so what v2 holds is always a time-ordered prefix — never a
 *        later write ahead of an earlier one it may depend on (a sale ahead of its client's create).
 *     What stays in v1 reaches v2 later — the next Supabase-master boot re-runs this, and B1's 409 on
 *     its replay converts it (`mirrorRefusalConversion.ts`) — or meets v1's own bounds.
 */
import {
  V1_EXCLUSIVE_WAIT_MS,
  V1OutboxBusyError,
  withExclusiveV1Queue as productionWithExclusiveV1Queue,
  type OutboxEntry as V1OutboxEntry,
} from '../Firebase/dualWriteOutbox'
import { captureOperationalEvent } from '../analytics/operationalTelemetry'
import { outboxV2DrainLockName } from './outboxDrainer'
import { openAppQuarantineStore, quarantinedAlert, reportQuarantined, type QuarantinedAlert } from './outboxQuarantine'
import type { NewOutboxEntry, OutboxEntryV2 } from './outboxEntry'
import {
  OutboxAppendIndeterminateError,
  OutboxDuplicateEntryError,
  type OutboxStore,
} from './outboxStore'
import { classifyV1Request, v2EntryFromV1Entry } from './v1ToV2'

// ============================================================================
// Appending a v1-derived entry (shared with the 409 conversion)
// ============================================================================

export type V1DerivedAppend =
  | { status: 'queued'; entry: OutboxEntryV2 }
  /** The store already holds a READABLE entry under this id: an earlier attempt landed. */
  | { status: 'already-queued' }
  /** The id is held by an unreadable record: this write is NOT recoverable from v2 (keep the v1 copy). */
  | { status: 'corrupt-duplicate' }
  /** Maybe queued, even after one retry under the same id. Never re-issue under another id. */
  | { status: 'indeterminate'; error: unknown }
  /** The store refused: nothing was queued. */
  | { status: 'rejected'; error: unknown }

/**
 * Append, resolving a duplicate id the way P2 requires (landed only when `existing === 'ok'`) and
 * retrying an indeterminate append ONCE under the same id (obligation 4). Never throws.
 */
export async function appendV1Derived(store: OutboxStore, entry: NewOutboxEntry): Promise<V1DerivedAppend> {
  const once = async (): Promise<V1DerivedAppend> => {
    try {
      // A fresh copy: the engines may spread it, and the caller's stays pristine.
      return { status: 'queued', entry: await store.append(JSON.parse(JSON.stringify(entry)) as NewOutboxEntry) }
    } catch (error) {
      if (error instanceof OutboxDuplicateEntryError) {
        return error.existing === 'ok' ? { status: 'already-queued' } : { status: 'corrupt-duplicate' }
      }
      if (error instanceof OutboxAppendIndeterminateError) return { status: 'indeterminate', error }
      return { status: 'rejected', error }
    }
  }
  const first = await once()
  return first.status === 'indeterminate' ? once() : first
}

// ============================================================================
// The migration
// ============================================================================

export type V1MigrationOutcome =
  /** No domain-W entry of this organization in v1 (nothing was written anywhere). */
  | 'nothing-to-migrate'
  /** Every candidate is in v2 and gone from v1. */
  | 'migrated'
  /** Some candidates stay in v1 (see `kept`). */
  | 'incomplete'
  /** The v1 locks could not be held in time; nothing was read or changed. */
  | 'busy'
  /** Storage failed outside the per-entry handling (v1 could not be read or saved). */
  | 'failed'

export interface V1MigrationResult {
  outcome: V1MigrationOutcome
  /** This organization's domain-W entries found in v1. */
  candidates: number
  /** Newly appended to v2 and removed from v1. */
  moved: number
  /** Found in v2 already (a re-run after a crash) and removed from v1. */
  alreadyInV2: number
  /** Absolute stock levels moved to the QUARANTINE (outside the drain queue) and removed from v1. */
  quarantined: number
  /** Candidates left in v1, by why. */
  kept: {
    notConvertible: number
    corruptDuplicate: number
    indeterminate: number
    /** Later entries of a document whose earlier entry stayed in v1 (they must not overtake it). */
    behindKept: number
    /** Not attempted after the store refused an append. */
    notAttempted: number
    rejected: number
  }
  /** This organization's entries whose route cannot be read: left in v1 (never assumed "not W"). */
  unclassifiable: number
  /** Copied entries that sit AFTER a newer live entry in v2 (rule 4's residual), read after the copy; null when v2 could not be listed. */
  orderAfterLive: number | null
  /** The first few validator refusals, for the report. */
  reasons: string[]
}

export interface V1MigrationDeps {
  withExclusiveV1Queue: typeof productionWithExclusiveV1Queue
  openStore: () => OutboxStore
  /** Where absolute stock levels go (review 1, S-3): never drained. */
  openQuarantineStore: () => OutboxStore
  /** Page: an absolute stock level was quarantined. */
  quarantined: (alert: QuarantinedAlert) => void
  report: (orgFirestoreId: string, result: V1MigrationResult) => void
  now?: () => number
  /**
   * How long to wait for the locks, in total: the v2 drain Web Lock (web) and then the v1 queue (default
   * `V1_EXCLUSIVE_WAIT_MS`). Past it the migration reports `busy` having changed nothing.
   */
  waitMs?: number
  /** Web Locks (default `navigator.locks` when present; `null` = none, native). */
  locks?: MigrationLockManager | null
}

type MigrationLockManager = {
  request: (name: string, options: { ifAvailable?: boolean }, cb: (lock: unknown) => Promise<unknown>) => Promise<unknown>
}

function defaultLocks(): MigrationLockManager | null {
  try {
    const nav = (globalThis as { navigator?: { locks?: MigrationLockManager } }).navigator
    return nav?.locks ?? null
  } catch {
    return null
  }
}

/**
 * Review 2, SF-1 (web): run `work` holding this organization's v2 DRAIN Web Lock — the one every tab's
 * drainer takes `ifAvailable` — WAITED for, so no tab can deliver a copy while its v1 original is still
 * queued. The barrier (`v1MigrationBarrier.ts`) covers this JS context; this lock covers the others.
 * Taken BEFORE the v1 locks: no path holds a v1 lock and then asks for the v2 drain lock, so the order
 * cannot deadlock. Bounded by `deadline`: if it is not granted in time the migration is `busy`, and a
 * late grant does nothing. Native has one JS context and no Web Locks: `work` runs directly.
 */
async function underV2DrainLock<T>(
  locks: MigrationLockManager | null,
  orgFirestoreId: string,
  deadline: number,
  work: () => Promise<T>,
): Promise<T> {
  if (!locks) return work()
  let started = false
  let abandoned = false
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const run = locks.request(outboxV2DrainLockName(orgFirestoreId), {}, async () => {
      if (abandoned) return { ran: false as const }
      started = true
      if (timer !== undefined) clearTimeout(timer)
      return { ran: true as const, value: await work() }
    }) as Promise<{ ran: true; value: T } | { ran: false }>
    const expired = new Promise<'expired'>((resolve) => {
      timer = setTimeout(
        () => {
          if (started) return
          abandoned = true
          resolve('expired')
        },
        Math.max(0, deadline - Date.now()),
      )
    })
    const outcome = await Promise.race([run, expired])
    if (outcome === 'expired' || !outcome.ran) throw new V1OutboxBusyError(Math.max(0, deadline - Date.now()))
    return outcome.value
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

const emptyResult = (outcome: V1MigrationOutcome): V1MigrationResult => ({
  outcome,
  candidates: 0,
  moved: 0,
  alreadyInV2: 0,
  quarantined: 0,
  kept: { notConvertible: 0, corruptDuplicate: 0, indeterminate: 0, behindKept: 0, notAttempted: 0, rejected: 0 },
  unclassifiable: 0,
  orderAfterLive: null,
  reasons: [],
})

const MAX_REASONS = 5

/**
 * How many of `copiedIds` sit AFTER a live entry newer than them in this organization's v2 queue: a
 * post-flip write ahead of a pre-flip one (rule 4's residual, counted — never silent). Read after the
 * copy, so an append by another tab DURING the copy is seen too. `null` when v2 cannot be listed.
 */
export async function countOrderAfterLive(
  store: OutboxStore,
  orgFirestoreId: string,
  copiedIds: ReadonlySet<string>,
): Promise<number | null> {
  if (copiedIds.size === 0) return 0
  try {
    let newestLiveBefore = Number.NEGATIVE_INFINITY
    let inverted = 0
    for (const slot of await store.list(orgFirestoreId)) {
      if (slot.status !== 'ok') continue
      const entry = slot.entry
      if (entry.origin === 'live') {
        newestLiveBefore = Math.max(newestLiveBefore, entry.enqueuedAt)
      } else if (copiedIds.has(entry.entryId) && entry.enqueuedAt < newestLiveBefore) {
        inverted += 1
      }
    }
    return inverted
  } catch {
    return null
  }
}

/**
 * Move this organization's domain-W v1 entries into outbox v2 (module docblock). Never throws; the
 * result says what happened and is reported through `deps.report`.
 */
export async function migrateV1EntriesToV2(orgFirestoreId: string, deps: V1MigrationDeps): Promise<V1MigrationResult> {
  const now = deps.now ?? Date.now
  const waitMs = deps.waitMs ?? V1_EXCLUSIVE_WAIT_MS
  const deadline = Date.now() + waitMs
  let result: V1MigrationResult
  try {
    result = await underV2DrainLock(deps.locks === undefined ? defaultLocks() : deps.locks, orgFirestoreId, deadline, () => deps.withExclusiveV1Queue(
      async (queue) => {
        const run = emptyResult('nothing-to-migrate')
        const candidates: Array<{ entry: V1OutboxEntry; position: number }> = []
        queue.forEach((entry, position) => {
          if (entry.orgFirestoreId !== orgFirestoreId) return
          const w = classifyV1Request(entry.request)
          if (w === 'unclassifiable') run.unclassifiable += 1
          else if (w === 'w') candidates.push({ entry, position })
        })
        run.candidates = candidates.length
        if (candidates.length === 0) return { queue: null, result: run }
        // Rule 4: oldest first; equal times in v1 queue order (enqueue order).
        candidates.sort((a, b) => a.entry.enqueuedAt - b.entry.enqueuedAt || a.position - b.position)

        const store = deps.openStore()
        const removable = new Set<string>()
        /** v2 entry ids copied into the drain queue (not the quarantine), for the order count. */
        const copied = new Set<string>()
        /** Documents with an entry left in v1: their later entries stay too (rule 8). */
        const keptDocuments = new Set<string>()
        let stopped = false
        for (const { entry } of candidates) {
          if (stopped) {
            run.kept.notAttempted += 1
            continue
          }
          if (keptDocuments.has(entry.firestoreId)) {
            run.kept.behindKept += 1
            continue
          }
          const built = v2EntryFromV1Entry(entry, 'v1-migrated', now())
          if (!built.ok) {
            run.kept.notConvertible += 1
            if (run.reasons.length < MAX_REASONS) run.reasons.push(built.reason)
            keptDocuments.add(entry.firestoreId)
            continue
          }
          // Rule 7 (review 1, S-3): an absolute stock level goes to the QUARANTINE, outside the drain
          // queue: never replayed, never in front of anything.
          // eslint-disable-next-line no-await-in-loop
          const appended = await appendV1Derived(built.heldAbsolute ? deps.openQuarantineStore() : store, built.entry)
          switch (appended.status) {
            case 'queued':
            case 'already-queued': {
              removable.add(entry.id)
              if (built.heldAbsolute) {
                run.quarantined += 1
                // Paged on a re-run too: a crash may have come between the copy and its page.
                try {
                  deps.quarantined(quarantinedAlert(built.entry, now()))
                } catch {
                  // A page that fails never stops the copy.
                }
              } else {
                copied.add(built.entry.entryId)
                if (appended.status === 'queued') run.moved += 1
                else run.alreadyInV2 += 1
              }
              break
            }
            // Storage, not the entry: stop, so what v2 holds stays a time-ordered prefix (rule 8).
            case 'corrupt-duplicate':
              run.kept.corruptDuplicate += 1
              stopped = true
              break
            case 'indeterminate':
              run.kept.indeterminate += 1
              stopped = true
              break
            case 'rejected':
              run.kept.rejected += 1
              stopped = true
              break
          }
        }
        run.orderAfterLive = await countOrderAfterLive(store, orgFirestoreId, copied)
        const keptCount =
          run.kept.notConvertible +
          run.kept.corruptDuplicate +
          run.kept.indeterminate +
          run.kept.behindKept +
          run.kept.notAttempted +
          run.kept.rejected
        run.outcome = keptCount === 0 ? 'migrated' : 'incomplete'
        // Rule 5: remove exactly what v2 (or the quarantine) holds, once, under the same lock; save
        // nothing if nothing moved.
        return {
          queue: removable.size === 0 ? null : queue.filter((entry) => !removable.has(entry.id)),
          result: run,
        }
      },
      // The same budget: what the v2 drain lock took is not given again.
      { waitMs: Math.max(1, deadline - Date.now()) },
    ))
  } catch (error) {
    result = emptyResult(error instanceof V1OutboxBusyError ? 'busy' : 'failed')
  }
  try {
    deps.report(orgFirestoreId, result)
  } catch {
    // Reporting never changes what was migrated.
  }
  return result
}

// ============================================================================
// Production wiring
// ============================================================================

/** The app's store, lazily (as `sessionOutboxV2.ts` does): a platform engine that fails to load is a store that cannot be opened. */
function openAppStore(): OutboxStore {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { openOutboxStore } = require('./store') as { openOutboxStore: () => OutboxStore }
  return openOutboxStore()
}

/** Telemetry: counts only (`operationalTelemetry`'s rule), and loud when anything stays behind. */
export function reportV1Migration(orgFirestoreId: string, result: V1MigrationResult): void {
  if (result.outcome === 'nothing-to-migrate' && result.unclassifiable === 0) return
  const properties = {
    outcome: result.outcome,
    candidates: result.candidates,
    moved: result.moved,
    already_in_v2: result.alreadyInV2,
    quarantined: result.quarantined,
    kept_not_convertible: result.kept.notConvertible,
    kept_corrupt_duplicate: result.kept.corruptDuplicate,
    kept_indeterminate: result.kept.indeterminate,
    kept_behind: result.kept.behindKept,
    kept_not_attempted: result.kept.notAttempted,
    kept_rejected: result.kept.rejected,
    unclassifiable: result.unclassifiable,
    order_after_live: result.orderAfterLive,
  }
  const line = JSON.stringify({ orgFirestoreId, ...properties, reasons: result.reasons })
  if (result.outcome === 'migrated' && result.unclassifiable === 0 && result.orderAfterLive === 0) {
    // eslint-disable-next-line no-console
    console.log('[outboxV2] v1 → v2 migration', line)
  } else {
    // eslint-disable-next-line no-console
    console.error('[outboxV2] v1 → v2 migration left work behind', line)
  }
  captureOperationalEvent('outbox_v1_migration', properties)
}

export function productionV1MigrationDeps(): V1MigrationDeps {
  return {
    withExclusiveV1Queue: productionWithExclusiveV1Queue,
    openStore: openAppStore,
    openQuarantineStore: openAppQuarantineStore,
    quarantined: reportQuarantined,
    report: reportV1Migration,
  }
}
